import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { publicAxios, dataFetcher, endpoints, clearAuthTokens } from 'Api';
import { AuthContext } from 'Context';
import logo from 'Assets/Login Logo.png';
import styles from './AddToProjectorModal.module.css';

// Where "Sign up" sends the user (opened in a new tab so this share page stays put).
const SIGNUP_PATH = '/newSign-Up';

// Persist the session exactly like the normal /newSign-in success handler does
// (token + upload token + uuid), and return the user token.
const persistSession = (resp) => {
  const token = resp?.data?.token;
  if (token) localStorage.setItem('token', token);
  if (resp?.data?.is_email_verified != null) {
    localStorage.setItem('isVerified', resp.data.is_email_verified);
  }
  if (resp?.api_response?.access) {
    localStorage.setItem('uploader_token', resp.api_response.access);
    try {
      const payload = JSON.parse(atob(resp.api_response.access.split('.')[1]));
      if (payload.exp) {
        localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
      }
    } catch (e) { /* token still usable; server is source of truth on expiry */ }
  }
  if (resp?.api_response?.uuid) localStorage.setItem('user_uuid', resp.api_response.uuid);
  return token;
};

const profileFrom = (resp, fallbackEmail) => {
  const d = resp?.data || {};
  const name = d.display_name
    || `${d.firstname || ''} ${d.lastname || ''}`.trim();
  return { name, email: d.email || fallbackEmail || '' };
};

// "Add to your Projector" modal for the public share page.
//  - Logged in  -> confirm screen (name + email from getMyProfile)
//  - Logged out -> email/password sign-in, then the same confirm screen
// The final "add" API is wired in callAddApi() (see TODO) once it's available.
const AddToProjectorModal = ({
  open, onClose, onView, itemTitle, itemType, summary, shareKey, ownerName,
}) => {
  // 'checking' | 'signin' | 'signingIn' | 'confirm' | 'adding' | 'success'
  const [step, setStep] = useState('checking');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [error, setError] = useState('');
  // Category add is sequential; { done, total } drives the progress bar.
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const activeRef = useRef(true);
  const failedRef = useRef(0);

  const isCategory = itemType === 'category';

  const auth = useContext(AuthContext) || {};

  // Reset + decide the starting step every time the modal opens.
  useEffect(() => {
    if (!open) return undefined;
    activeRef.current = true;
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setError('');
    setProfile({ name: '', email: '' });

    const token = localStorage.getItem('token');
    if (token) {
      setStep('checking');
      dataFetcher(endpoints.getMyProfile)
        .then((resp) => {
          if (!activeRef.current) return;
          if (resp?.success && resp?.data) {
            setProfile(profileFrom(resp));
            setStep('confirm');
          } else {
            // Token no longer valid — fall back to sign-in.
            setStep('signin');
          }
        })
        .catch(() => { if (activeRef.current) setStep('signin'); });
    } else {
      setStep('signin');
    }

    return () => { activeRef.current = false; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isBusy = step === 'signingIn' || step === 'adding';
  const canSubmit = email.trim() !== '' && password.trim() !== '';

  const handleSignin = async (e) => {
    e.preventDefault();
    if (!canSubmit || isBusy) return;
    setError('');
    setStep('signingIn');
    try {
      const resp = (await publicAxios.post(endpoints.signin, {
        email: email.trim(),
        password,
        affliateCode: null,
        token: null,
      })).data;

      if (resp?.success && resp?.data?.token) {
        const token = persistSession(resp);
        auth.setToken?.(token);
        auth.forceAutoSignin?.();

        // Grab the display name/email for the confirm screen (best-effort).
        let prof = { name: '', email: email.trim() };
        try {
          const profResp = await dataFetcher(endpoints.getMyProfile);
          if (profResp?.success) prof = profileFrom(profResp, email.trim());
        } catch (_) { /* fall back to the entered email */ }

        if (!activeRef.current) return;
        setProfile(prof);
        setStep('confirm');
      } else {
        setError(resp?.message || 'Sign in failed. Check your email and password.');
        setStep('signin');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Sign in failed. Please try again.');
      setStep('signin');
    }
  };

  // Copy a single shared item (video/album, or one video of a category via
  // draft_id). Token is attached by the axios interceptor. Throws on failure.
  const addOne = async (extra) => {
    const resp = await dataFetcher(endpoints.addItemToMyProjector, { key: shareKey, ...extra });
    if (resp && resp.success === false) throw new Error(resp.message || 'add failed');
    return resp;
  };

  // A category can hold hundreds of videos. There is no "add category" call —
  // we fetch the full draft_id list once, then copy the videos one at a time
  // (addItemToMyProjector + draft_id), updating the progress bar as we go so
  // the user always sees forward motion.
  const addCategoryVideos = async () => {
    const listResp = (await publicAxios.post(
      endpoints.getSharedCategoryDraftIds,
      { key: shareKey },
    )).data;

    // Be tolerant of the response envelope: array of ids or array of objects.
    const raw = listResp?.data?.draft_ids
      || listResp?.draft_ids
      || (Array.isArray(listResp?.data) ? listResp.data : [])
      || [];
    const draftIds = (Array.isArray(raw) ? raw : [])
      .map((x) => (x && typeof x === 'object' ? (x.draft_id ?? x.id) : x))
      .filter((x) => x != null && x !== '');

    if (!activeRef.current) return;
    setProgress({ done: 0, total: draftIds.length });

    let failed = 0;
    for (let i = 0; i < draftIds.length; i += 1) {
      if (!activeRef.current) return;
      try {
        // Sequential by design — the backend processes one draft at a time.
        // eslint-disable-next-line no-await-in-loop
        await addOne({ draft_id: draftIds[i] });
      } catch (e) {
        failed += 1;
      }
      if (!activeRef.current) return;
      setProgress({ done: i + 1, total: draftIds.length });
    }
    failedRef.current = failed;
  };

  const handleAdd = async () => {
    if (isBusy) return;
    setError('');
    failedRef.current = 0;
    setProgress({ done: 0, total: 0 });
    setStep('adding');
    try {
      if (isCategory) {
        await addCategoryVideos();
      } else {
        await addOne();
      }
      if (!activeRef.current) return;
      setStep('success');
    } catch (err) {
      if (!activeRef.current) return;
      setError(err?.response?.data?.message || 'Could not add to your Projector. Please try again.');
      setStep('confirm');
    }
  };

  const openSignup = () => window.open(SIGNUP_PATH, '_blank', 'noopener');

  // Log out of the current account and drop back to the sign-in form.
  const handleUseDifferentAccount = () => {
    clearAuthTokens();
    auth.removeToken?.();
    setProfile({ name: '', email: '' });
    setEmail('');
    setPassword('');
    setError('');
    setStep('signin');
  };

  const avatarInitial = (profile.name?.[0] || profile.email?.[0] || '?').toUpperCase();

  const successMessage = () => {
    if (isCategory) {
      const { total } = progress;
      const added = total - failedRef.current;
      if (failedRef.current > 0) {
        return `${added} of ${total} videos added. ${failedRef.current} couldn’t be copied — try again to finish.`;
      }
      return `${added} ${added === 1 ? 'video is' : 'videos are'} now in your library.`;
    }
    return itemTitle ? `“${itemTitle}” is now in your library.` : 'It’s now in your library.';
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={styles.overlay}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !isBusy) onClose?.(); }}
    >
      <div className={styles.card} role="dialog" aria-modal="true">

        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
          disabled={isBusy}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className={styles.brand}>
          <img src={logo} alt="Projector" className={styles.brandLogo} />
        </div>

        {/* ---- CHECKING LOGIN ---- */}
        {step === 'checking' && (
          <div className={styles.statusState}>
            <div className={styles.spinner} />
            <p className={styles.statusText}>Checking your account…</p>
          </div>
        )}

        {/* ---- SIGN IN FORM ---- */}
        {step === 'signin' && (
          <form className={styles.form} onSubmit={handleSignin}>
            <h2 className={styles.title}>Sign in to Projector</h2>
            <p className={styles.subtitle}>
              {summary ? (
                <>
                  Add
                  {' '}
                  <span className={styles.itemName}>{summary}</span>
                  {' '}
                  to your library
                  {ownerName ? ` from ${ownerName}` : ''}
                  .
                </>
              ) : (
                'Sign in to add this to your library.'
              )}
            </p>

            <label className={styles.field}>
              <span className={styles.label}>Email</span>
              <input
                type="email"
                className={styles.input}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Password</span>
              <div className={styles.passwordWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={styles.input}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className={styles.peek}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>

            {error && <p className={styles.errorText}>{error}</p>}

            <button type="submit" className={styles.submit} disabled={!canSubmit}>
              Sign in
            </button>

            <p className={styles.switchLine}>
              Don&apos;t have an account?
              {' '}
              <button type="button" className={styles.switchBtn} onClick={openSignup}>
                Sign up
              </button>
            </p>
          </form>
        )}

        {/* ---- CONFIRM ---- */}
        {step === 'confirm' && (
          <div className={styles.form}>
            <h2 className={styles.title}>Add to your Projector?</h2>
            <p className={styles.subtitle}>
              {summary ? (
                <>
                  You&apos;re about to add
                  {' '}
                  <span className={styles.itemName}>{summary}</span>
                  {ownerName ? ` from ${ownerName}` : ''}
                  {' '}
                  to your library.
                </>
              ) : (
                'This will be added to your library.'
              )}
            </p>

            <div className={styles.userChip}>
              <div className={styles.userAvatar}>{avatarInitial}</div>
              <div className={styles.userMeta}>
                {profile.name ? <span className={styles.userName}>{profile.name}</span> : null}
                <span className={styles.userEmail}>{profile.email}</span>
              </div>
            </div>

            {error && <p className={styles.errorText}>{error}</p>}

            <div className={styles.confirmActions}>
              <button type="button" className={styles.submit} onClick={handleAdd}>
                Yes, add to my Projector
              </button>
              <button
                type="button"
                className={styles.doneBtn}
                onClick={handleUseDifferentAccount}
              >
                Use a different account
              </button>
              <button type="button" className={styles.doneBtn} onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ---- SIGNING IN ---- */}
        {step === 'signingIn' && (
          <div className={styles.statusState}>
            <div className={styles.spinner} />
            <p className={styles.statusText}>Signing you in…</p>
          </div>
        )}

        {/* ---- ADDING ---- */}
        {step === 'adding' && (
          isCategory ? (
            <div className={styles.addingState}>
              <div className={styles.spinner} />
              <p className={styles.statusText}>Adding to your Projector…</p>
              {progress.total > 0 ? (
                <>
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                    />
                  </div>
                  <p className={styles.progressCount}>
                    {progress.done}
                    {' of '}
                    {progress.total}
                    {' videos added'}
                  </p>
                </>
              ) : (
                <>
                  <div className={styles.progressTrack}>
                    <div className={styles.progressIndeterminate} />
                  </div>
                  <p className={styles.statusSub}>Preparing your collection…</p>
                </>
              )}
              <p className={styles.addingNote}>
                Large collections can take a little while — please keep this window open.
              </p>
            </div>
          ) : (
            <div className={styles.statusState}>
              <div className={styles.spinner} />
              <p className={styles.statusText}>Adding to your Projector…</p>
              {summary && <p className={styles.statusSub}>{summary}</p>}
            </div>
          )
        )}

        {/* ---- SUCCESS ---- */}
        {step === 'success' && (
          <div className={styles.successState}>
            <div className={styles.successCircle}>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className={styles.successTitle}>Added to your Projector!</h2>
            <p className={styles.successSub}>{successMessage()}</p>
            <div className={styles.successActions}>
              <button type="button" className={styles.viewBtn} onClick={onView}>
                View your Projector
              </button>
              <button type="button" className={styles.doneBtn} onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddToProjectorModal;
